import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Devider,
  Flex,
  List,
  Pagination,
  PaginationInfoLabel,
  SearchContext,
  Spinner,
  Text,
} from 'dash-ui';
import { useTheme } from 'styled-components';

import { MediaFragment, useGetPropertyByIdQuery } from 'gql/generated.graphql';

import MediaFileItem from './components/MediaFileItem/MediaFileItem';
import { ListWrapper, EmptyContainer } from './MediaFilesList.styles';

const MediaFilesList = ({
  propertyId,
  folderId,
  isSlideshowFolder,
  media,
  total,
  setFileToPreviewId,
}: {
  propertyId: string;
  folderId?: string;
  isSlideshowFolder?: boolean;
  setFileToPreviewId: (id: string) => void;
  media: {
    folders: MediaFragment[];
    files: MediaFragment[];
  };
  total: {
    filesTotal: number;
    foldersTotal: number;
  };
}) => {
  const { t } = useTranslation(['common', 'pages']);
  const { data: propertyData, loading: propertyLoading } =
    useGetPropertyByIdQuery({ variables: { propertyId } }) || {};
  const { slideshowMediaFolderId } = propertyData?.getPropertyById || {};

  const { searchSlug } = useContext(SearchContext);
  const theme = useTheme();

  const loading = propertyLoading;

  function getPlaceholderText() {
    if (searchSlug) return t`common:placeholders.noSearchResults`;
    if (folderId && isSlideshowFolder)
      return t`pages:propertyPage.noFilesPlaceholder`;

    return t`pages:propertyMediaFolderPage.noFilesPlaceholder`;
  }

  return (
    <Box w="100%">
      <ListWrapper px={6} pos="relative">
        {media.folders.length > 0 && (
          <Box>
            <Box mt={4} mb={6}>
              <Text medium color={theme.colors.n70}>
                {t('common:labels.folders', { number: total.foldersTotal })}
              </Text>
            </Box>
            <List data={media.folders} containerClassName="list">
              {(item) => (
                <MediaFileItem
                  media={item}
                  deletable={slideshowMediaFolderId !== item._id}
                />
              )}
            </List>
          </Box>
        )}
        <Box my={6}>
          <Text medium color={theme.colors.n70}>
            {t('common:labels.files', { number: total.filesTotal })}
          </Text>
        </Box>
        {media.files.length > 0 ? (
          <List data={media.files} containerClassName="list" isDraggable={true}>
            {(item) => (
              <MediaFileItem
                onClick={() => setFileToPreviewId(item._id)}
                media={item}
              />
            )}
          </List>
        ) : (
          <EmptyContainer
            py={24}
            w="100%"
            justifyContent="center"
            alignItems="center"
          >
            <Box w={440}>
              <Text color={theme.colors.n70}>{getPlaceholderText()}</Text>
            </Box>
          </EmptyContainer>
        )}
        {loading && <Spinner size={32} fullContent />}
      </ListWrapper>
      <Devider mt={2} />
      <Flex p={6} justifyContent="space-between" alignItems="center">
        <PaginationInfoLabel
          label={t('pages:propertyPage.paginationMediaLabel')}
          color={theme.colors.n70}
        />
        <Pagination
          previousButtonLabel={t('common:labels.previous')}
          nextButtonLabel={t('common:labels.next')}
        />
      </Flex>
    </Box>
  );
};

export default MediaFilesList;
